import React, { useState, useEffect } from "react";
import { apiService, handleApiError } from "../services/api";
import { reviews as fallbackReviews } from "../mock/data";
import { Star, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [currentReview, setCurrentReview] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const data = await apiService.getReviews();
        // Limit to first 5 Checkatrade reviews
        setReviews(data.slice(0, 5));
        setError(null);
      } catch (err) {
        console.error('Failed to fetch reviews, using fallback data:', err);
        const errorInfo = handleApiError(err);
        setError(errorInfo.message);
        // Use fallback data if API fails, limited to 5 reviews
        setReviews(fallbackReviews.slice(0, 5));
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-700" />
            <p className="mt-4 text-gray-600">Loading customer reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!reviews.length) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-gray-600">No reviews available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Checkatrade Reviews Header */}
        <div className="text-center mb-16">
          {error && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 max-w-md mx-auto">
              ⚠️ Using cached data: {error}
            </div>
          )}
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center">
                {/* Checkatrade Logo - Orange background with white text */}
                <div className="bg-orange-500 text-white px-4 py-2 rounded-lg font-bold text-lg flex items-center">
                  <span className="mr-2">✓</span>
                  <span>Checkatrade</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">4.9</div>
              <div className="flex justify-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                ))}
              </div>
              <div className="text-gray-600">Based on 50+ Checkatrade reviews</div>
            </div>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  {[...Array(reviews[currentReview].rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                  ))}
                </div>
                <Badge variant="outline" className="text-green-700 border-green-200">
                  {reviews[currentReview].service}
                </Badge>
              </div>
              
              <blockquote className="text-lg text-gray-700 leading-relaxed mb-6 italic text-center">
                "{reviews[currentReview].text}"
              </blockquote>
              
              <div className="text-center">
                <div className="font-semibold text-gray-900">
                  {reviews[currentReview].name}
                </div>
                <div className="text-sm text-gray-500">
                  {reviews[currentReview].date}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <Button
            variant="outline"
            size="icon"
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white border-green-200 hover:bg-green-50"
            onClick={prevReview}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white border-green-200 hover:bg-green-50"
            onClick={nextReview}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Review Dots */}
        <div className="flex justify-center mt-8 space-x-2">
          {reviews.map((_, index) => (
            <button
              key={index}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentReview ? 'bg-green-700' : 'bg-gray-300'
              }`}
              onClick={() => setCurrentReview(index)}
            />
          ))}
        </div>

        {/* View All Reviews Link */}
        <div className="text-center mt-8">
          <a
            href="https://www.checkatrade.com/trades/pnmgardening"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-white border-2 border-green-700 text-green-700 font-semibold rounded-lg hover:bg-green-700 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <span className="mr-2">View All Reviews on Checkatrade</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        {/* Bottom Text */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            THE Balham Gardeners FOR professional gardening services
          </p>
          <p className="text-gray-600 max-w-3xl mx-auto">
            We offer a comprehensive range of gardening services across Balham and surrounding London areas, 
            tailored to meet the diverse needs of our clients. Whether you need regular garden maintenance 
            or a one-off tidy-up, our skilled gardeners are committed to delivering exceptional results every time.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;